/**
 * Authored, fixed-order copy for /{locale}/pcc/services/$service.
 * Replaces rotated pools that claimed unverifiable volumes, fixed SLAs,
 * "no RFE" acceptance and fixed prices. {name} = service name (English).
 */
import type { PccLocale } from "@/data/pcc/locale-copy";

export interface SafePccCopy {
  h1: string;
  lead: string;
  sections: Array<{ h: string; body: string }>;
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  accepted: string;
  acceptedNote: string;
  daysValue: string;
}

export const SAFE_PCC_COPY: Record<PccLocale, SafePccCopy> = {
  en: {
    h1: "{name} — Thai Police Clearance Support",
    lead: "Support for {name}: our staff check your purpose, the receiving authority's rules and your documents first, then prepare a quotation. The certificate itself is issued by the Royal Thai Police; we do not guarantee issuance, timing or acceptance abroad.",
    sections: [
      { h: "Who issues the certificate", body: "Thai criminal record certificates are issued by the Royal Thai Police after fingerprint and identity checks. Our role is limited to preparing the file, liaising and coordinating any translation or legalisation you need." },
      { h: "Documents usually needed", body: "A passport or Thai ID, evidence of your stay in Thailand for foreigners, the purpose or receiving authority, and a power of attorney if someone applies on your behalf. Exact requirements are confirmed against current official guidance for your case." },
      { h: "Translation, legalisation and validity", body: "Some receivers need a translation, Thai Ministry of Foreign Affairs legalisation, embassy legalisation or, once applicable, an Apostille. Many also set their own validity window. Check the receiver's latest rules before you apply." },
      { h: "Fees and timing", body: "We do not publish fixed service prices. Contact our team for a quotation. Government fees are shown separately where applicable. Timing depends on the police queue, fingerprint checks and any downstream steps, so dates are confirmed per case." },
    ],
    faqs: [
      { q: "Will {name} be accepted by my embassy or immigration office?", a: "Acceptance is decided only by the receiving authority. We help you match their published requirements but cannot promise acceptance." },
      { q: "How long does it take?", a: "It varies with the police queue and any translation or legalisation steps. We confirm an estimate after reviewing your documents." },
      { q: "Do I have to attend in person?", a: "Fingerprinting and identity checks follow police procedure at the time. Where an alternative channel exists for your situation, we explain it before you commit." },
      { q: "How much does it cost?", a: "Contact our staff for a quotation. Official government fees are separate." },
      { q: "How is my personal data protected?", a: "Your data is used only for this application under Thailand's PDPA, with access limited to staff handling the case." },
    ],
    steps: [
      { tag: "Ask", text: "Send your passport page, purpose and destination via LINE, email or phone." },
      { tag: "Check", text: "Staff check current official requirements and list the documents you need." },
      { tag: "Quote", text: "You receive a quotation and an estimated timeline for your case." },
      { tag: "Apply", text: "The application is lodged following police procedure; we update you on progress." },
      { tag: "Deliver", text: "The certificate, plus any agreed translation or legalisation, is delivered as agreed." },
    ],
    accepted: "Receivers that commonly request this certificate",
    acceptedNote: "This list is not a promise of acceptance; always check the receiver's current rules.",
    daysValue: "Confirmed per case",
  },
  zh: {
    h1: "{name} — 泰国无犯罪证明协助",
    lead: "{name} 协助服务：工作人员先确认用途、接收机构的规定和您的文件，再出具报价。证书由泰国皇家警察签发，我们不保证签发结果、时间或海外受理。",
    sections: [
      { h: "证书由谁签发", body: "泰国无犯罪记录证明由泰国皇家警察在核对指纹和身份后签发。我们仅负责整理文件、联络，以及协调所需的翻译或认证。" },
      { h: "通常需要的文件", body: "护照或泰国身份证、外国人在泰居留证明、用途或接收机构，以及代办时的授权书。具体要求按最新官方指引逐案确认。" },
      { h: "翻译、认证与有效期", body: "部分接收方要求翻译、泰国外交部认证、使馆认证或（适用后）海牙认证，并自行规定有效期。申请前请查阅接收方的最新规定。" },
      { h: "费用与时间", body: "我们不公布固定服务价格，请联系我们的团队获取报价。政府规费（如适用）另行列明。时间取决于警方排队、指纹核对及后续步骤，按个案确认。" },
    ],
    faqs: [
      { q: "{name} 会被我的使馆或移民局接受吗？", a: "是否接受仅由接收机构决定。我们协助您对照其公布的要求，但无法保证受理。" },
      { q: "需要多长时间？", a: "视警方排队及翻译、认证步骤而定，审核文件后提供预估时间。" },
      { q: "必须亲自到场吗？", a: "指纹和身份核对按当时的警方程序办理。如您的情况有其他渠道，我们会事先说明。" },
      { q: "费用是多少？", a: "请联系工作人员获取报价，政府规费另计。" },
      { q: "个人资料如何保护？", a: "资料仅依泰国 PDPA 用于本次申请，仅限经办人员访问。" },
    ],
    steps: [
      { tag: "咨询", text: "通过 LINE、电邮或电话发送护照页、用途及目的地。" },
      { tag: "核对", text: "工作人员核对最新官方要求并列出所需文件。" },
      { tag: "报价", text: "您收到报价及个案预估时间。" },
      { tag: "申请", text: "按警方程序递交申请，我们随时通报进度。" },
      { tag: "交付", text: "按约定交付证书及约定的翻译或认证文件。" },
    ],
    accepted: "通常要求此证明的机构",
    acceptedNote: "此列表并非受理承诺，请务必查阅接收方的最新规定。",
    daysValue: "按个案确认",
  },
  ja: {
    h1: "{name} — タイ無犯罪証明の取得サポート",
    lead: "{name} のサポートです。担当者が用途、提出先の要件、書類を先に確認し、お見積りを作成します。証明書はタイ王国警察が発行するもので、発行・期間・海外での受理は保証できません。",
    sections: [
      { h: "証明書の発行機関", body: "タイの無犯罪証明は、指紋と本人確認の後にタイ王国警察が発行します。当社の役割は書類準備、連絡調整、必要な翻訳や認証の手配に限られます。" },
      { h: "通常必要な書類", body: "パスポートまたはタイ ID、外国人の場合はタイ滞在の証明、用途または提出先、代理申請の場合は委任状。正確な要件は最新の公式案内に基づき案件ごとに確認します。" },
      { h: "翻訳・認証・有効期間", body: "提出先によっては翻訳、タイ外務省認証、大使館認証、または適用後のアポスティーユが必要で、独自の有効期間を定めることもあります。申請前に最新要件をご確認ください。" },
      { h: "料金と期間", body: "固定のサービス料金は掲載していません。お見積りは担当チームまでお問い合わせください。官公庁手数料は該当する場合に別途表示します。期間は警察の処理状況や後続手続きにより異なるため、案件ごとに確定します。" },
    ],
    faqs: [
      { q: "{name} は大使館や入国管理局で受理されますか？", a: "受理は提出先のみが判断します。公表要件に合わせるお手伝いはしますが、受理は保証できません。" },
      { q: "期間はどのくらいですか？", a: "警察の処理状況や翻訳・認証の有無で異なります。書類確認後に目安をお伝えします。" },
      { q: "本人の出頭は必要ですか？", a: "指紋と本人確認はその時点の警察手続きに従います。別の方法がある場合は事前にご説明します。" },
      { q: "料金はいくらですか？", a: "担当者にお見積りをご依頼ください。官公庁手数料は別途です。" },
      { q: "個人情報はどう保護されますか？", a: "タイ PDPA に基づき本申請のみに使用し、担当者以外のアクセスを制限します。" },
    ],
    steps: [
      { tag: "相談", text: "パスポートのページ、用途、提出国を LINE・メール・電話でお送りください。" },
      { tag: "確認", text: "担当者が最新の公式要件を確認し、必要書類をご案内します。" },
      { tag: "見積", text: "お見積りと案件ごとの目安期間をお受け取りください。" },
      { tag: "申請", text: "警察の手続きに従って申請し、進捗をお知らせします。" },
      { tag: "納品", text: "証明書と合意した翻訳・認証書類を約束どおりお渡しします。" },
    ],
    accepted: "この証明書をよく求める提出先",
    acceptedNote: "この一覧は受理を約束するものではありません。提出先の最新要件を必ずご確認ください。",
    daysValue: "案件ごとに確定",
  },
  ko: {
    h1: "{name} — 태국 범죄경력증명 발급 지원",
    lead: "{name} 지원 서비스입니다. 담당자가 용도, 제출처 규정, 서류를 먼저 확인한 뒤 견적을 드립니다. 증명서는 태국 왕립경찰이 발급하며, 발급 여부·기간·해외 수리를 보장하지 않습니다.",
    sections: [
      { h: "증명서 발급 기관", body: "태국 범죄경력증명은 지문과 신원 확인 후 태국 왕립경찰이 발급합니다. 저희 역할은 서류 준비, 연락 조정, 필요한 번역이나 인증 주선에 한정됩니다." },
      { h: "일반적으로 필요한 서류", body: "여권 또는 태국 신분증, 외국인의 경우 태국 체류 증빙, 용도 또는 제출처, 대리 신청 시 위임장. 정확한 요건은 최신 공식 안내에 따라 건별로 확인합니다." },
      { h: "번역·인증·유효기간", body: "일부 제출처는 번역, 태국 외교부 인증, 대사관 인증 또는 적용 시 아포스티유를 요구하며 자체 유효기간을 정합니다. 신청 전 최신 규정을 확인하세요." },
      { h: "비용과 기간", body: "고정 서비스 가격을 게시하지 않습니다. 견적은 담당팀에 문의해 주세요. 정부 수수료는 해당 시 별도로 표시합니다. 기간은 경찰 처리 상황과 후속 절차에 따라 달라 건별로 확정합니다." },
    ],
    faqs: [
      { q: "{name}이 대사관이나 이민국에서 받아들여지나요?", a: "수리 여부는 제출처만 결정합니다. 공개된 요건에 맞추도록 돕지만 수리를 보장할 수는 없습니다." },
      { q: "얼마나 걸리나요?", a: "경찰 처리 상황과 번역·인증 여부에 따라 다르며, 서류 검토 후 예상 기간을 안내합니다." },
      { q: "직접 방문해야 하나요?", a: "지문과 신원 확인은 당시 경찰 절차를 따릅니다. 다른 방법이 있으면 미리 설명드립니다." },
      { q: "비용은 얼마인가요?", a: "담당자에게 견적을 문의해 주세요. 정부 수수료는 별도입니다." },
      { q: "개인정보는 어떻게 보호되나요?", a: "태국 PDPA에 따라 이 신청에만 사용하며 담당자 외 접근을 제한합니다." },
    ],
    steps: [
      { tag: "문의", text: "여권 면, 용도, 제출 국가를 LINE, 이메일 또는 전화로 보내 주세요." },
      { tag: "확인", text: "담당자가 최신 공식 요건을 확인하고 필요 서류를 안내합니다." },
      { tag: "견적", text: "견적과 건별 예상 기간을 받으십니다." },
      { tag: "신청", text: "경찰 절차에 따라 신청하고 진행 상황을 알려 드립니다." },
      { tag: "전달", text: "증명서와 합의한 번역·인증 서류를 약속대로 전달합니다." },
    ],
    accepted: "이 증명서를 자주 요구하는 제출처",
    acceptedNote: "이 목록은 수리를 보장하지 않습니다. 제출처의 최신 규정을 반드시 확인하세요.",
    daysValue: "건별 확정",
  },
  vi: {
    h1: "{name} — Hỗ trợ lý lịch tư pháp Thái Lan",
    lead: "Hỗ trợ {name}: nhân viên kiểm tra mục đích, quy định của cơ quan tiếp nhận và giấy tờ của bạn trước, rồi lập báo giá. Giấy chứng nhận do Cảnh sát Hoàng gia Thái Lan cấp; chúng tôi không bảo đảm việc cấp, thời gian hay sự chấp nhận ở nước ngoài.",
    sections: [
      { h: "Ai cấp giấy chứng nhận", body: "Giấy xác nhận lý lịch tư pháp Thái Lan do Cảnh sát Hoàng gia Thái Lan cấp sau khi kiểm tra vân tay và danh tính. Vai trò của chúng tôi chỉ là chuẩn bị hồ sơ, liên lạc và điều phối dịch thuật hoặc hợp pháp hóa nếu cần." },
      { h: "Giấy tờ thường cần", body: "Hộ chiếu hoặc CCCD Thái, bằng chứng cư trú tại Thái với người nước ngoài, mục đích hoặc cơ quan tiếp nhận, và giấy ủy quyền nếu nhờ người khác nộp. Yêu cầu chính xác được xác nhận theo hướng dẫn chính thức hiện hành cho từng trường hợp." },
      { h: "Dịch thuật, hợp pháp hóa và hiệu lực", body: "Một số nơi nhận yêu cầu bản dịch, hợp pháp hóa của Bộ Ngoại giao Thái Lan, của đại sứ quán hoặc Apostille khi áp dụng, và tự quy định thời hạn hiệu lực. Hãy kiểm tra quy định mới nhất trước khi nộp." },
      { h: "Phí và thời gian", body: "Chúng tôi không công bố giá dịch vụ cố định. Liên hệ đội ngũ của chúng tôi để nhận báo giá. Lệ phí nhà nước được ghi riêng nếu có. Thời gian phụ thuộc hàng chờ của cảnh sát và các bước tiếp theo nên được xác nhận theo từng trường hợp." },
    ],
    faqs: [
      { q: "{name} có được đại sứ quán hoặc cơ quan di trú chấp nhận không?", a: "Việc chấp nhận chỉ do cơ quan tiếp nhận quyết định. Chúng tôi giúp bạn đối chiếu yêu cầu đã công bố nhưng không thể cam kết." },
      { q: "Mất bao lâu?", a: "Tùy hàng chờ của cảnh sát và bước dịch thuật, hợp pháp hóa. Chúng tôi báo ước tính sau khi xem giấy tờ." },
      { q: "Tôi có phải trực tiếp có mặt không?", a: "Lấy vân tay và kiểm tra danh tính theo thủ tục cảnh sát tại thời điểm đó. Nếu có kênh khác phù hợp, chúng tôi giải thích trước." },
      { q: "Chi phí bao nhiêu?", a: "Liên hệ nhân viên để nhận báo giá. Lệ phí nhà nước tính riêng." },
      { q: "Dữ liệu cá nhân được bảo vệ thế nào?", a: "Chỉ dùng cho hồ sơ này theo PDPA Thái Lan, giới hạn cho nhân viên phụ trách." },
    ],
    steps: [
      { tag: "Hỏi", text: "Gửi trang hộ chiếu, mục đích và nước đến qua LINE, email hoặc điện thoại." },
      { tag: "Kiểm tra", text: "Nhân viên kiểm tra yêu cầu chính thức hiện hành và liệt kê giấy tờ cần có." },
      { tag: "Báo giá", text: "Bạn nhận báo giá và thời gian ước tính cho trường hợp của mình." },
      { tag: "Nộp", text: "Hồ sơ được nộp theo thủ tục cảnh sát; chúng tôi cập nhật tiến độ." },
      { tag: "Giao", text: "Giao giấy chứng nhận cùng bản dịch hoặc hợp pháp hóa đã thỏa thuận." },
    ],
    accepted: "Nơi thường yêu cầu giấy này",
    acceptedNote: "Danh sách này không phải cam kết chấp nhận; luôn kiểm tra quy định hiện hành của bên nhận.",
    daysValue: "Xác nhận theo từng trường hợp",
  },
  id: {
    h1: "{name} — Bantuan SKCK Thailand",
    lead: "Bantuan {name}: staf memeriksa tujuan, aturan instansi penerima, dan dokumen Anda terlebih dahulu, lalu menyiapkan penawaran harga. Sertifikat diterbitkan oleh Kepolisian Kerajaan Thailand; kami tidak menjamin penerbitan, waktu, atau penerimaan di luar negeri.",
    sections: [
      { h: "Siapa yang menerbitkan sertifikat", body: "Surat keterangan catatan kriminal Thailand diterbitkan oleh Kepolisian Kerajaan Thailand setelah pemeriksaan sidik jari dan identitas. Peran kami terbatas pada penyiapan berkas, koordinasi, serta pengaturan terjemahan atau legalisasi bila diperlukan." },
      { h: "Dokumen yang biasanya diperlukan", body: "Paspor atau KTP Thailand, bukti tinggal di Thailand bagi WNA, tujuan atau instansi penerima, dan surat kuasa bila diwakilkan. Persyaratan pasti dikonfirmasi sesuai panduan resmi terbaru untuk kasus Anda." },
      { h: "Terjemahan, legalisasi, dan masa berlaku", body: "Sebagian penerima meminta terjemahan, legalisasi Kementerian Luar Negeri Thailand, legalisasi kedutaan, atau Apostille bila berlaku, dan menetapkan masa berlaku sendiri. Periksa aturan terbaru sebelum mengajukan." },
      { h: "Biaya dan waktu", body: "Kami tidak menampilkan harga layanan tetap. Hubungi tim kami untuk penawaran harga. Biaya pemerintah ditampilkan terpisah bila berlaku. Waktu bergantung pada antrean kepolisian dan langkah lanjutan, sehingga dikonfirmasi per kasus." },
    ],
    faqs: [
      { q: "Apakah {name} akan diterima kedutaan atau imigrasi saya?", a: "Penerimaan hanya diputuskan oleh instansi penerima. Kami membantu mencocokkan persyaratan yang dipublikasikan, tetapi tidak dapat menjaminnya." },
      { q: "Berapa lama prosesnya?", a: "Tergantung antrean kepolisian dan langkah terjemahan atau legalisasi. Perkiraan diberikan setelah dokumen diperiksa." },
      { q: "Apakah saya harus hadir sendiri?", a: "Sidik jari dan pemeriksaan identitas mengikuti prosedur kepolisian saat itu. Bila ada jalur lain untuk situasi Anda, kami jelaskan lebih dulu." },
      { q: "Berapa biayanya?", a: "Hubungi staf kami untuk penawaran harga. Biaya resmi pemerintah terpisah." },
      { q: "Bagaimana data pribadi saya dilindungi?", a: "Hanya digunakan untuk permohonan ini sesuai PDPA Thailand, dengan akses terbatas pada staf yang menangani." },
    ],
    steps: [
      { tag: "Tanya", text: "Kirim halaman paspor, tujuan, dan negara tujuan via LINE, email, atau telepon." },
      { tag: "Periksa", text: "Staf memeriksa persyaratan resmi terbaru dan mendaftar dokumen yang diperlukan." },
      { tag: "Penawaran", text: "Anda menerima penawaran harga dan perkiraan waktu untuk kasus Anda." },
      { tag: "Ajukan", text: "Permohonan diajukan sesuai prosedur kepolisian; kami mengabarkan perkembangannya." },
      { tag: "Serahkan", text: "Sertifikat beserta terjemahan atau legalisasi yang disepakati diserahkan sesuai perjanjian." },
    ],
    accepted: "Penerima yang umumnya meminta sertifikat ini",
    acceptedNote: "Daftar ini bukan jaminan penerimaan; selalu periksa aturan terbaru penerima.",
    daysValue: "Dikonfirmasi per kasus",
  },
};
